// 瀏覽器端：索引的 lazy 載入與快取、鄰居表、最近搜尋、分析事件。浮層與完整頁共用。
// 首頁初次載入不會碰這支（只有開啟浮層、或 hover／focus 搜尋 icon 預取、或進 /search/ 才會 import 並 fetch）。
import { createEngine } from './engine.mjs'
import { expandRelated } from './related.mjs'
import { trackEvent } from '../runtime/analytics.mjs'

const DATA = '/search/'
const engines = new Map()   // 'lite-zh' → Promise<engine>
let relatedPromise, metaPromise

// cache: 'no-cache'：每次都帶 ETag 回伺服器確認（沒變就是 304，幾百 bytes）。
// _headers 給 /search/*.json 的 stale-while-revalidate 會讓瀏覽器先拿舊索引——2026-10-03 部署地層 Strata 後，
// 舊索引裡的 /zh/lab/2024/#… 被點出去就是 404。索引網址不帶版本號，所以新鮮度只能靠每次確認。
const fetchJson = async url => {
  const res = await fetch(url, { cache: 'no-cache' })
  if (!res.ok) throw new Error(`${url}: HTTP ${res.status}`)
  return res.json()
}

// tier：'lite'（浮層）或 'full'（完整頁，含正文）。失敗時清掉快取，下次再試。
export function loadEngine(tier, locale) {
  const key = `${tier}-${locale}`
  if (!engines.has(key)) {
    const promise = fetchJson(`${DATA}${key}.json`).then(createEngine)
    promise.catch(() => engines.delete(key))
    engines.set(key, promise)
  }
  return engines.get(key)
}
export const prefetchSearch = locale => { loadEngine('lite', locale).catch(() => {}) }
export const loadRelated = () => (relatedPromise ||= fetchJson(DATA + 'related.json').then(expandRelated).catch(() => { relatedPromise = undefined; return expandRelated(null) }))
export const loadMeta = () => (metaPromise ||= fetchJson(DATA + 'meta.json').catch(() => { metaPromise = undefined; return null }))

// 最近搜尋：localStorage 只是便利功能，存取失敗（無痕、被封鎖）就當沒有
const RECENT_KEY = 'cyw-search-recent', RECENT_MAX = 6
export function getRecent() {
  try { const list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]'); return Array.isArray(list) ? list.filter(s => typeof s === 'string').slice(0, RECENT_MAX) : [] } catch { return [] }
}
export function addRecent(term) {
  term = String(term || '').trim().slice(0, 100)
  if (!term) return getRecent()
  const list = [term, ...getRecent().filter(s => s !== term)].slice(0, RECENT_MAX)
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)) } catch {}
  return list
}
export function clearRecent() { try { localStorage.removeItem(RECENT_KEY) } catch {} return [] }

// 分析：search 在同一個詞只送一次（Enter、點結果、或停止輸入 1.5 秒）；query 只放 search_term，不放進任何網址。
const sentTerms = new Set()
export function trackSearch(term) {
  term = String(term || '').trim().toLowerCase().slice(0, 100)
  if (!term || sentTerms.has(term)) return false
  sentTerms.add(term)
  return trackEvent('search', { search_term: term })
}
export const trackSelect = ({ kind, id, surface, position }) => trackEvent('select_content', { content_type: kind, item_id: id, surface, position })

export const isExternal = u => /^https?:\/\//.test(u)
